import { create } from "zustand";
import { persist } from "zustand/middleware";
import type { User } from "@/api/types";
import { setAuthToken, setRefreshToken } from "@/api/client";

type PersistedUser = Omit<User, "accessToken" | "expiry">;

interface AuthStore {
  user: User | null;
  setUser: (user: User) => void;
  clearUser: () => void;
}

export const useAuthStore = create<AuthStore>()(
  persist(
    (set) => ({
      user: null,
      setUser: (user) => set({ user }),
      clearUser: () => set({ user: null }),
    }),
    {
      name: "auth-store",
      // Bump when the persisted User shape changes. Older versions are
      // dropped via migrate(), forcing a fresh login on the next visit.
      version: 2,
      migrate: () => ({ user: null }),
      // accessToken and expiry are intentionally excluded — access tokens must
      // not be stored in localStorage (XSS risk). On reload the 401 interceptor
      // in client.ts uses storedRefreshToken to obtain a fresh access token.
      partialize: (state): { user: PersistedUser | null } => ({
        user: state.user
          ? {
              name: state.user.name,
              login: state.user.login,
              refreshToken: state.user.refreshToken,
              groups: state.user.groups,
              activeGroupId: state.user.activeGroupId,
            }
          : null,
      }),
      onRehydrateStorage: () => (state) => {
        if (state?.user?.refreshToken) {
          setRefreshToken(state.user.refreshToken);
        }
      },
    },
  ),
);

// Cross-tab sync: listen for localStorage changes made by other tabs.
// The "storage" event only fires on OTHER tabs, never the tab that wrote.
if (typeof window !== "undefined") {
  window.addEventListener("storage", (event) => {
    if (event.key !== "auth-store") return;

    const { user: currentUser } = useAuthStore.getState();

    if (!event.newValue) {
      // localStorage was cleared entirely
      if (currentUser) {
        setAuthToken(null);
        setRefreshToken(null);
        useAuthStore.getState().clearUser();
      }
      return;
    }

    try {
      const parsed = JSON.parse(event.newValue);
      const newUser: User | null = parsed?.state?.user ?? null;

      if (!newUser && currentUser) {
        // Other tab logged out — only act if we are currently logged in
        setAuthToken(null);
        setRefreshToken(null);
        useAuthStore.getState().clearUser();
      } else if (
        newUser &&
        (!currentUser || newUser.refreshToken !== currentUser.refreshToken)
      ) {
        // Other tab refreshed tokens or different user logged in.
        // accessToken is not in localStorage — clear the stale token so the
        // next API call triggers a fresh refresh via the 401 interceptor.
        setAuthToken(null);
        setRefreshToken(newUser.refreshToken);
        useAuthStore.getState().setUser(newUser as unknown as User);
      }
    } catch {
      // Malformed JSON in storage event — ignore
    }
  });
}
